import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface Pond {
  id: string;
  name: string;
  type: string;
  isPublic: boolean;
  inviteCode: string | null;
  ownerId: string | null;
  isOwner: boolean;
  memberCount?: number;
  parentId: string | null;
  requiresApproval: boolean;
  notifyOnJoin: boolean;
}

export const usePonds = () => {
  const [ponds, setPonds] = useState<Pond[]>([]);
  const [currentPondId, setCurrentPondId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchPonds = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // Fetch owned ponds
      const { data: ownedPonds, error: ownedError } = await supabase
        .from("ponds")
        .select("*")
        .eq("owner_id", user.id);

      if (ownedError) throw ownedError;

      // Fetch joined ponds (as member)
      const { data: memberships, error: memberError } = await supabase
        .from("pond_members")
        .select("pond_id")
        .eq("user_id", user.id);

      if (memberError) throw memberError;

      const memberPondIds = memberships?.map((m) => m.pond_id) || [];

      let joinedPonds: any[] = [];
      if (memberPondIds.length > 0) {
        const { data, error } = await supabase
          .from("ponds")
          .select("*")
          .in("id", memberPondIds);
        if (error) throw error;
        joinedPonds = data || [];
      }

      const allPonds: Pond[] = [
        ...(ownedPonds || []).map((p) => ({
          id: p.id,
          name: p.name,
          type: p.type,
          isPublic: p.is_public || false,
          inviteCode: p.invite_code,
          ownerId: p.owner_id,
          isOwner: true,
          parentId: p.parent_id,
          requiresApproval: p.requires_approval || false,
          notifyOnJoin: p.notify_on_join || false,
        })),
        ...joinedPonds.map((p) => ({
          id: p.id,
          name: p.name,
          type: p.type,
          isPublic: p.is_public || false,
          inviteCode: p.invite_code,
          ownerId: p.owner_id,
          isOwner: false,
          parentId: p.parent_id,
          requiresApproval: p.requires_approval || false,
          notifyOnJoin: p.notify_on_join || false,
        })),
      ];

      setPonds(allPonds);

      // Set default pond (private one)
      const privatePond = allPonds.find((p) => p.type === "private" && p.isOwner);
      if (privatePond && !currentPondId) {
        setCurrentPondId(privatePond.id);
      }
    } catch (error: any) {
      console.error("Error fetching ponds:", error);
      toast.error("Erro ao carregar lagoas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPonds();
  }, [user]);

  const createPond = async (
    name: string, 
    isPublic: boolean = false, 
    parentId: string | null = null,
    requiresApproval: boolean = false,
    notifyOnJoin: boolean = false
  ) => {
    if (!user) {
      toast.error("Usuário não autenticado");
      return null;
    }

    try {
      // Trigger will generate invite_code automatically
      const { data, error } = await supabase
        .from("ponds")
        .insert({
          name,
          type: "shared",
          owner_id: user.id,
          is_public: isPublic,
          parent_id: parentId,
          requires_approval: requiresApproval,
          notify_on_join: isPublic && !requiresApproval ? notifyOnJoin : false,
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating pond:", error);
        throw error;
      }

      toast.success("Lagoa criada! 🌿");
      await fetchPonds();
      return data;
    } catch (error: any) {
      console.error("Error creating pond:", error);
      const errorMessage = error?.message || "Erro desconhecido ao criar lagoa";
      toast.error(`Erro ao criar lagoa: ${errorMessage}`);
      return null;
    }
  };

  const joinPond = async (inviteCode: string) => {
    if (!user) return false;

    try {
      const { data, error } = await supabase.functions.invoke('join-pond-by-code', {
        body: { invite_code: inviteCode },
      });

      if (error) {
        console.error("Error joining pond:", error);
        toast.error("Erro ao entrar na lagoa");
        return false;
      }

      if (data?.error) {
        toast.error(data.error);
        return false;
      }

      if (data?.pending_approval) {
        toast.success(`Solicitação enviada para a lagoa "${data.pond_name}"! Aguarde aprovação do dono.`);
        return true;
      }

      if (data?.joined) {
        toast.success(`Você entrou na lagoa "${data.pond_name}"! 🐸`);
        await fetchPonds();
        return true;
      }

      return false;
    } catch (error: any) {
      console.error("Error joining pond:", error);
      toast.error("Erro ao entrar na lagoa");
      return false;
    }
  };

  const searchPublicPonds = async (searchTerm: string) => {
    try {
      const { data, error } = await supabase
        .from("ponds")
        .select("id, name, type, is_public, owner_id, parent_id")
        .eq("is_public", true)
        .ilike("name", `%${searchTerm}%`)
        .limit(20);

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error("Error searching ponds:", error);
      toast.error("Erro ao buscar lagoas");
      return [];
    }
  };

  const updatePond = async (
    pondId: string,
    updates: {
      name?: string;
      isPublic?: boolean;
      requiresApproval?: boolean;
      notifyOnJoin?: boolean;
    }
  ) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from("ponds")
        .update({
          name: updates.name,
          is_public: updates.isPublic,
          requires_approval: updates.requiresApproval,
          notify_on_join: updates.notifyOnJoin,
        })
        .eq("id", pondId)
        .eq("owner_id", user.id); // Ensure user is owner

      if (error) throw error;

      toast.success("Lagoa atualizada! 🌿");
      await fetchPonds();
      return true;
    } catch (error: any) {
      console.error("Error updating pond:", error);
      toast.error("Erro ao atualizar lagoa");
      return false;
    }
  };

  const currentPond = ponds.find((p) => p.id === currentPondId);

  return {
    ponds,
    currentPond,
    currentPondId,
    setCurrentPondId,
    loading,
    createPond,
    joinPond,
    updatePond,
    searchPublicPonds,
    refetch: fetchPonds,
  };
};
